import React from 'react';
import { Flex } from 'antd';
import { faShare } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { CircledFontAwesomeIcon } from 'jf/components/CircledFontAwesomeIcon';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExExpandableAlert, DevExExpandableAlertType } from 'jf/components/DevExExpandableAlert';
import { DevExModal } from 'jf/components/DevExModal';
import { DevExText } from 'jf/components/DevExText';
import { useStudyMSTeamsTargetUpdater } from 'jf/pages/studyBuilder/targetRespondents/msteams/useStudyMSTeamsTargetUpdater';
import { useStudySlackTargetUpdater } from 'jf/pages/studyBuilder/targetRespondents/useStudySlackTargetUpdater';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { getDevExEnv } from 'jf/utils/getDevExEnv';
import { useClientFlags } from 'jf/utils/useClientFlags';

export const MessagingDisconnectedModal: React.FC = () => {
  const modal = useJFOverlay(MessagingDisconnectedModal);
  const { studyRef } = useParams<{ studyRef: string }>();
  const theme = useDevExTheme();
  const navigate = useNavigate();
  const { msTeamsTargeting } = useClientFlags();
  const devExEnv = getDevExEnv();

  const studySlackTargetUpdater = useStudySlackTargetUpdater(studyRef);
  const studyMsteamsTargetUpdater = useStudyMSTeamsTargetUpdater(studyRef);
  const { msgProvider, msgProviderLower, msgIcon } = useTargetingClient();

  const onCancel = () => {
    trackEvent(`survey-analyzer:${msgProviderLower}-disconnected:dismiss`);
    modal.close();
    navigate('/studies');
  };

  const onConnect = () => {
    trackEvent(`survey-analyzer:${msgProviderLower}-disconnected:reconnect`);
    window.open(`${devExEnv.appOrigin}/settings/plugins`, '_blank');
  };

  const onRevert = () => {
    trackEvent(`survey-analyzer:${msgProviderLower}-disconnected:revert`);
    // delete all existing messaging targets to revert study to "Share link" mode
    if (msTeamsTargeting) {
      studyMsteamsTargetUpdater.update([]).then(() => modal.close());
    } else {
      studySlackTargetUpdater.update([]).then(() => modal.close());
    }
  };

  return (
    <DevExModal
      open={modal.isOpened}
      onCancel={onCancel}
      title={`${msgProvider} Disconnected`}
      footer={null}
      width={650}
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <DevExExpandableAlert
          type={DevExExpandableAlertType.WARNING}
          title={`The Jellyfish ${msgProvider} app has been uninstalled. To continue, you must either reinstall the ${msgProvider} app or revert to manual distribution.`}
        />

        <Flex gap={theme.variable.spacing.md}>
          <DevExCard style={{ flex: 1 }} onClick={onConnect}>
            <Flex vertical align="center" gap={theme.variable.spacing.md}>
              <CircledFontAwesomeIcon icon={msgIcon} style={{ color: theme.color.text.tertiary }} />
              <DevExText fontSize="md">Reconnect {msgProvider} App</DevExText>
            </Flex>
          </DevExCard>

          <DevExCard style={{ flex: 1 }} onClick={onRevert}>
            <Flex vertical align="center" gap={theme.variable.spacing.md}>
              <CircledFontAwesomeIcon icon={faShare} style={{ color: theme.color.text.tertiary }} />
              <DevExText fontSize="md"> I'll share the link myself</DevExText>
            </Flex>
          </DevExCard>
        </Flex>
      </Flex>
    </DevExModal>
  );
};
